import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { IMAGES, MENU } from '../data/menuData';
import Footer from '../components/Footer';
import './Menu.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function Stars({ stars }) {
  return (
    <div className="stars">
      {[1,2,3,4,5].map(i => {
        const full = i <= Math.floor(stars);
        const half = !full && i - 0.5 <= stars;
        return (
          <span key={i} className={full ? '' : half ? 'star-half' : 'star-dim'}>★</span>
        );
      })}
    </div>
  );
}

function SpiceDots({ level = 0 }) {
  return (
    <div className="spice-dots">
      {[1,2,3].map(i => <div key={i} className={`spice-dot${i > level ? ' off' : ''}`} />)}
    </div>
  );
}

const TABS = ['buffet', 'rice', 'kottu', 'devilled', 'chapsuey', 'pizza', 'sides'];

export default function Menu() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [activeTab, setActiveTab]   = useState('buffet');
  const [apiDishes, setApiDishes]   = useState(null); // null = still loading

  useEffect(() => {
    fetch(`${API_URL}/dishes?withPhotos=1`)
      .then(r => r.json())
      .then(setApiDishes)
      .catch(() => setApiDishes([])); // API down → empty array → fall back to static
  }, []);

  const cat = MENU[activeTab];
  const isGrid = !!cat?.dishes; // rice / kottu / devilled use grid cards

  // null while loading → show static data; array when loaded → use API
  const tabDishes = apiDishes !== null
    ? apiDishes.filter(d => d.category === activeTab)
    : null;

  const gridDishes  = tabDishes ?? cat?.dishes  ?? [];
  const simpleDishes = tabDishes ?? cat?.simple ?? [];

  return (
    <div className="menu-page">
      {/* Hero banner */}
      <div className="menu-hero">
        <div className="section-label center">{t('menu.label')}</div>
        <h1>{t('menu.title')}</h1>
        <p>{t('menu.desc')}</p>
      </div>

      {/* Sticky category nav */}
      <div className="cat-nav">
        {TABS.map(tab => (
          <button
            key={tab}
            className={`cat-nav-btn${activeTab === tab ? ' active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {t(`tab.${tab}`)}
          </button>
        ))}
      </div>

      <div className="menu-content">

        {/* Featured hero dish */}
        {cat && cat.heroImg && (
          <div className="dish-hero">
            <div className="dish-hero-bg" style={{ backgroundImage: `url(${IMAGES[cat.heroImg]})` }} />
            <div className="dish-hero-overlay" />
            <div className="dish-hero-body">
              <div>
                {cat.tagKey && <div className="dish-hero-tag">{t(cat.tagKey)}</div>}
                <h3>{cat.heroName || t(cat.nameKey)}</h3>
                <p>{cat.heroDesc || t(cat.descKey)}</p>
              </div>
              <div className="dish-hero-right">
                <div className="dish-hero-price">{cat.heroPrice}</div>
                {cat.spice > 0 && <SpiceDots level={cat.spice} />}
                <button className="btn-order" onClick={() => navigate('/reservations')}>
                  {t('btn.order')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Category heading */}
        {cat && (cat.heading || cat.subtitle) && (
          <div className="cat-heading" style={!cat.heroImg ? {marginTop:'2rem'} : {}}>
            {cat.heading  && <h2>{cat.heading}</h2>}
            {cat.subtitle && <span>{cat.subtitle}</span>}
          </div>
        )}

        {/* Grid dish cards */}
        {cat && isGrid && (
          <div className="dish-list">
            {gridDishes.map((d, i) => (
              <div key={d.id ?? i} className="dish-item">
                <div
                  className="dish-item-photo"
                  style={{ backgroundImage: d.photo_data
                    ? `url(${d.photo_data})`
                    : d.img
                      ? `url(${IMAGES[d.img]})`
                      : 'none' }}
                />
                <div className="dish-item-body">
                  <h4>{d.name}</h4>
                  <p>{d.description || d.desc}</p>
                  <div className="dish-item-footer">
                    <span className="dish-price">{d.price}</span>
                    {d.stars  && <Stars stars={d.stars} />}
                    {d.spice !== undefined && !d.stars && <SpiceDots level={d.spice} />}
                    <button className="dish-add" onClick={() => navigate('/reservations')}>+</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Simple list items */}
        {cat && !isGrid && (
          <div className="dish-simple-list">
            {simpleDishes.map((d, i) => (
              <div key={d.id ?? i} className="dish-simple">
                <div>
                  <h4>{d.name}</h4>
                  {(d.description || d.desc || d.descKey) && (
                    <p>{d.description || d.desc || t(d.descKey)}</p>
                  )}
                </div>
                <span className="dish-simple-price">{d.price}</span>
              </div>
            ))}
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
}
